
import React from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { PlusCircle, FileText, CheckCircle } from 'lucide-react';
import { useDashboard } from '@/hooks/useDashboard';
import StatCard from '@/components/dashboard/StatCard';
import AnalysesChart from '@/components/dashboard/AnalysesChart';
import RecentProcurements from '@/components/dashboard/RecentProcurements';

const Dashboard = () => {
  const navigate = useNavigate();
  const { 
    userName, 
    stats, 
    recentProcurements, 
    chartData, 
    loading 
  } = useDashboard();

  const renderSkeletonUI = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="h-5 bg-gray-300 rounded w-1/2 mb-4"></div>
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gray-300 rounded-full mr-3"></div>
              <div className="h-8 bg-gray-300 rounded w-12"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-pulse">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-6">
          <div className="h-5 bg-gray-300 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
          <div className="h-[300px] bg-gray-100 rounded"></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="h-5 bg-gray-300 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border-b pb-3 last:border-0">
                <div className="h-5 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="flex justify-between mt-1">
                  <div className="h-4 bg-gray-300 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <h1 className="text-2xl font-bold text-darkgray">
            {userName ? `Välkommen, ${userName}` : 'Välkommen till din dashboard'}
          </h1>
          <Button 
            onClick={() => navigate('/new-procurement')}
            className="mt-4 sm:mt-0 flex items-center gap-2"
          >
            <PlusCircle size={16} />
            <span>Ny upphandling</span>
          </Button>
        </div>

        {loading ? renderSkeletonUI() : (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <StatCard 
                title="Aktiva upphandlingar" 
                value={stats.activeProcurements} 
                icon={<FileText className="h-full w-full" />}
              />
              
              <StatCard 
                title="Genomförda analyser" 
                value={stats.completedAnalyses} 
                icon={<CheckCircle className="h-full w-full" />}
                iconColor="text-green-500"
              />
              
              <StatCard 
                title="Sparade dokument" 
                value={stats.savedDocuments} 
                icon={<FileText className="h-full w-full" />}
                iconColor="text-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <AnalysesChart data={chartData} />
              <RecentProcurements procurements={recentProcurements} />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
